import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Logo } from './Logo';

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const langs = ['fr', 'en', 'nl'];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleHome = (e) => {
    setOpen(false);
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };


  const navLink = 'text-white hover:text-[#111111] font-oswald uppercase tracking-widest text-sm transition-colors duration-200 font-semibold';
  const activeLink = 'text-[#111111]';

  return (
    <nav className={`fixed top-0 w-full z-50 bg-[#F5820D] border-b border-black/10 transition-all duration-300 ${scrolled ? 'shadow-xl shadow-black/25' : ''}`} data-testid="navbar">
      <div className={`max-w-7xl mx-auto px-3 flex items-center justify-between transition-all duration-300 ${scrolled ? 'py-1' : 'py-2'}`}>
        <Link to="/" onClick={handleHome} className="flex items-center gap-2 flex-shrink-0">
          <Logo size="sm" variant="color" />
          <span className="font-barlow font-black uppercase text-[#111111] text-2xl tracking-wide leading-none self-center">Ultra Sport</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          <Link to="/" onClick={handleHome} className={`${navLink} ${location.pathname === '/' ? activeLink : ''}`} data-testid="nav-home">
            {t('nav.home')}
          </Link>
          <Link to="/produits" className={`${navLink} ${location.pathname === '/produits' ? activeLink : ''}`} data-testid="nav-products">
            {t('nav.products')}
          </Link>
          <Link to="/a-propos" className={`${navLink} ${location.pathname === '/a-propos' ? activeLink : ''}`} data-testid="nav-about">
            {t('nav.about')}
          </Link>
          <Link to="/contact" onClick={() => setOpen(false)} className={`${navLink} ${location.pathname === '/contact' ? activeLink : ''}`} data-testid="nav-contact">
            {t('nav.contact')}
          </Link>
        </div>

        {/* Language switcher */}
        <div className="hidden md:flex items-center gap-0.5 border border-white/40 rounded-sm px-2 py-1.5">
          {langs.map((lang, i) => (
            <span key={lang} className="flex items-center">
              <button
                onClick={() => i18n.changeLanguage(lang)}
                className={`text-xs font-bold uppercase tracking-wider px-1.5 transition-colors ${
                  i18n.language === lang ? 'text-[#111111]' : 'text-white/80 hover:text-white'
                }`}
                data-testid={`lang-${lang}`}
              >
                {lang.toUpperCase()}
              </button>
              {i < langs.length - 1 && <span className="text-white/40 text-xs">|</span>}
            </span>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-white p-1"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-[#F5820D] border-t border-black/10 px-6 py-5 flex flex-col gap-5">
          <Link to="/" onClick={handleHome} className={`${navLink} text-base`}>{t('nav.home')}</Link>
          <Link to="/produits" onClick={() => setOpen(false)} className={`${navLink} text-base`}>{t('nav.products')}</Link>
          <Link to="/a-propos" onClick={() => setOpen(false)} className={`${navLink} text-base`}>{t('nav.about')}</Link>
          <Link to="/contact" onClick={() => setOpen(false)} className={`${navLink} text-base`}>{t('nav.contact')}</Link>
          <div className="flex gap-4 pt-3 border-t border-black/10">
            {langs.map(lang => (
              <button
                key={lang}
                onClick={() => { i18n.changeLanguage(lang); setOpen(false); }}
                className={`text-sm font-bold uppercase tracking-wider ${i18n.language === lang ? 'text-[#111111]' : 'text-white/80'}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};
